import { useState, useEffect } from 'react';
import { Mail, Plus, Loader2, AlertCircle, CheckCircle2, Clock, XCircle, Info } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface DomainEntry {
  id: string;
  domain: string;
  status: 'pending' | 'approved' | 'rejected';
  proposed_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
  proposed_by_email: string | null;
  proposed_by_name: string | null;
  reviewed_by_email: string | null;
  reviewed_by_name: string | null;
}

const statusBadge = {
  pending: { label: 'Venter på godkjenning', cls: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Godkjent', cls: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Avvist', cls: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function MemberEmailDomainManagement({ memberId }: { memberId: string }) {
  const [domains, setDomains] = useState<DomainEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDomains();
  }, [memberId]);

  async function loadDomains() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/email-domains`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke laste domener');
      setDomains(d.domains || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/email-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain.trim().replace(/^@/, ''), notes: notes.trim() || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess(d.status === 'approved'
        ? `Domenet er godkjent.`
        : 'Domeneforslaget er sendt til studieforbundet for godkjenning. Du får e-post når det er behandlet.');
      setNewDomain('');
      setNotes('');
      setShowForm(false);
      await loadDomains();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Mail size={22} />
            E-postdomener
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Godkjente e-postdomener kontrollerer hvem som kan inviteres som brukere i organisasjonen.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-kf-primary text-white px-4 py-2 rounded-lg hover:bg-kf-primary-dark transition"
          >
            <Plus size={18} />
            Foreslå nytt domene
          </button>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="m-0 mb-1"><strong>Slik fungerer det:</strong></p>
          <ul className="m-0 pl-5 text-sm space-y-0.5">
            <li>Du foreslår domener (f.eks. <code>eksempel.no</code>)</li>
            <li>Studieforbundet ditt godkjenner eller avviser forslaget</li>
            <li>Brukere kan kun inviteres med e-post fra godkjente domener (sensorer kan ha ekstern e-post)</li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 m-0">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 m-0">{success}</p>
        </div>
      )}

      {/* Forslag-skjema */}
      {showForm && (
        <form onSubmit={handleSubmit} className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3">Foreslå nytt e-postdomene</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domene</label>
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="eksempel.no"
                pattern="[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-600 mt-1">Skriv kun domene-delen (f.eks. <code>eksempel.no</code>, ikke <code>@eksempel.no</code>).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Begrunnelse (valgfritt)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="F.eks. 'Hovedorganisasjonen vår' eller 'Avdelingskontor i Oslo'"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setNewDomain(''); setNotes(''); setError(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={submitting || !newDomain}
                className="px-4 py-2 bg-kf-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send forslag
              </button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : domains.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
          <Mail className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>Ingen domener registrert ennå.</p>
          <p className="text-sm">Foreslå et domene for å kunne invitere brukere.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {domains.map((d) => {
            const meta = statusBadge[d.status];
            const Icon = meta.icon;
            return (
              <div key={d.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="text-gray-900 font-mono">@{d.domain}</strong>
                      <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${meta.cls}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Foreslått {new Date(d.proposed_at).toLocaleDateString('nb-NO')}
                      {d.proposed_by_name ? ` av ${d.proposed_by_name}` : ''}
                    </div>
                    {d.notes && (
                      <p className="text-sm text-gray-600 mt-1 italic">{d.notes}</p>
                    )}
                    {d.status === 'rejected' && d.rejection_reason && (
                      <div className="text-sm text-red-700 mt-1 bg-red-50 border border-red-200 rounded p-2">
                        <strong>Avvist:</strong> {d.rejection_reason}
                      </div>
                    )}
                    {d.reviewed_at && d.reviewed_by_name && (
                      <div className="text-xs text-gray-500 mt-1">
                        {d.status === 'approved' ? 'Godkjent' : 'Behandlet'} av {d.reviewed_by_name}
                        {' '}({new Date(d.reviewed_at).toLocaleDateString('nb-NO')})
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
